import { useEffect, useRef } from 'react';

import { Button, Stack } from '@mui/material';

import DeviceResetCard from './DeviceReset/DeviceResetCard';
import ResetProgress from './DeviceReset/ResetProgress';
import DrawerBody from './DrawerBody';
import DrawerFooter from './DrawerFooter';
import { DrawerComponentProps } from './DrawerManager';
import DrawerTitle from './DrawerTitle';
import { useAnalytics } from 'src/AnalyticsContext';
import usePendingDeviceTriggers from 'src/hooks/usePendingDeviceTriggers';
import type { Task, Unit } from 'src/store/sageAdminApi';
import { useGetTaskQuery } from 'src/store/sageAdminApi';

const SuccessDelayMs = 2_000;
const PollingInterval = 1_000;

const DrawerName = 'Device Reset';

export interface DeviceResetDrawerProps extends DrawerComponentProps {
    task: Task;
    unit?: Unit;
}

function DeviceResetDrawer(props: DeviceResetDrawerProps) {
    const { onNext, onClose, task } = props;

    const { trackEvent } = useAnalytics();

    const taskId = task.id!;

    const { data: polledTask } = useGetTaskQuery(
        { taskId },
        {
            pollingInterval: PollingInterval,
            refetchOnFocus: true,
            refetchOnMountOrArgChange: true,
            skip: !taskId,
        }
    );
    const currentTask = polledTask ?? task;

    const { deviceTriggerStates, isReset } = usePendingDeviceTriggers(currentTask);

    const hasAdvanced = useRef(false);

    const deviceCount = deviceTriggerStates.length;
    const allocationTypes = [
        ...new Set(
            deviceTriggerStates.flatMap((device) =>
                device.allocationType ? [device.allocationType] : []
            )
        ),
    ];

    useEffect(() => {
        if (!isReset || hasAdvanced.current) {
            return undefined;
        }

        const timeout = setTimeout(() => {
            hasAdvanced.current = true;
            trackEvent('Care Confirmed', {
                taskId,
                context: DrawerName,
                deviceCount,
                allocationTypes,
            });

            onNext({ task: currentTask });
        }, SuccessDelayMs);

        return () => clearTimeout(timeout);
        // disable exhaustive deps since allocationTypes changes every render
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isReset]);

    const isMultiDeviceAlert = deviceTriggerStates.length > 1;

    const handleClose = () => {
        trackEvent('Care Confirmation Cancelled', {
            taskId,
            context: DrawerName,
            deviceCount,
            allocationTypes,
            pendingDeviceCount: deviceTriggerStates.filter((device) => device.isPending).length,
        });
        onClose();
    };

    return (
        <>
            <DrawerTitle
                title={
                    isMultiDeviceAlert
                        ? 'Reset all devices to continue'
                        : 'Reset the device to continue'
                }
            />
            <DrawerBody>
                <Stack sx={{ gap: 1.5 }}>
                    {isMultiDeviceAlert && <ResetProgress devices={deviceTriggerStates} />}
                    {deviceTriggerStates.map((device) => (
                        <DeviceResetCard
                            key={device.deviceId}
                            device={device}
                            isMultiDeviceAlert={isMultiDeviceAlert}
                        />
                    ))}
                </Stack>
            </DrawerBody>
            {!isReset && (
                <DrawerFooter>
                    <Button variant="text" fullWidth onClick={handleClose}>
                        Go Back
                    </Button>
                </DrawerFooter>
            )}
        </>
    );
}

export default DeviceResetDrawer;
